import React from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Global Loader Component
 * A reusable loading spinner with Lucide icons and Tailwind CSS
 *
 * @param {Object} props
 * @param {boolean} props.loading - Whether to show the loader
 * @param {string} props.message - Optional loading message
 * @param {string} props.size - Size variant: 'sm', 'md', 'lg', 'xl', 'fullscreen'
 * @param {boolean} props.fullscreen - Whether to show as fullscreen overlay
 * @param {string} props.className - Additional CSS classes
 */
export const Loader = ({
  loading = false,
  message,
  size = 'md',
  fullscreen = false,
  className,
}) => {
  if (!loading) return null;

  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-12 h-12',
    fullscreen: 'w-16 h-16',
  };

  const spinner = (
    <div className={cn('flex flex-col items-center justify-center space-y-4', className)}>
      <Loader2
        className={cn(
          'animate-spin text-[hsl(var(--brand-green))]',
          sizeClasses[size] || sizeClasses.md
        )}
      />
      {message && <p className="text-lg font-medium text-gray-600 dark:text-gray-400">{message}</p>}
    </div>
  );

  if (fullscreen) {
    return (
      <div className="fixed inset-0 z-[9999] bg-white dark:bg-gray-900 flex items-center justify-center">
        {spinner}
      </div>
    );
  }

  return spinner;
};

/**
 * Inline Loader Component
 * A smaller inline loader for buttons and small spaces
 */
export const InlineLoader = ({ className, size = 'sm' }) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  return (
    <Loader2
      className={cn(
        'animate-spin text-[hsl(var(--brand-green))]',
        sizeClasses[size] || sizeClasses.sm,
        className
      )}
    />
  );
};

/**
 * Button Loader Component
 * A loader specifically designed for buttons
 */
export const ButtonLoader = ({ className }) => {
  return <Loader2 className={cn('w-4 h-4 animate-spin text-current', className)} />;
};

export default Loader;
